import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useConsentStore } from '../../stores/useConsentStore'
import { applyConsent } from '../../services/consent'

function Toggle({ checked, onChange, id }) {
  return (
    <button
      id={id}
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-10 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2 ${checked ? 'bg-green-500' : 'bg-gray-300'}`}
    >
      <span
        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition-transform duration-200 ${checked ? 'translate-x-4' : 'translate-x-0'}`}
      />
    </button>
  )
}

function CategoryRow({ id, label, description, checked, onChange, disabled }) {
  return (
    <div className="flex items-start justify-between gap-4 py-3">
      <div className="flex-1 min-w-0">
        <label htmlFor={id} className="text-sm font-medium text-gray-900 cursor-pointer">
          {label}
        </label>
        <p className="text-xs text-gray-500 mt-0.5">{description}</p>
      </div>
      {disabled ? (
        <span className="text-xs text-gray-400 font-medium whitespace-nowrap pt-0.5">Toujours actif</span>
      ) : (
        <Toggle id={id} checked={checked} onChange={onChange} />
      )}
    </div>
  )
}

export default function CookieBanner() {
  const { acceptAll, rejectAll, savePreferences, hasConsented } = useConsentStore()
  const [showDetails, setShowDetails] = useState(false)
  const [analytique, setAnalytique] = useState(false)
  const [marketing, setMarketing] = useState(false)

  if (hasConsented()) return null

  const handleAcceptAll = () => {
    acceptAll()
    applyConsent({ analytique: true, marketing: true })
  }

  const handleRejectAll = () => {
    rejectAll()
    applyConsent({ analytique: false, marketing: false })
  }

  const handleSave = () => {
    savePreferences({ analytique, marketing })
    applyConsent({ analytique, marketing })
  }

  return (
    <div
      role="dialog"
      aria-label="Gestion des cookies"
      aria-modal="false"
      className="fixed bottom-0 inset-x-0 z-[9999] p-4 sm:p-6"
    >
      <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
        {/* En-tête */}
        <div className="p-5 sm:p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-2">
            Nous respectons votre vie privée
          </h2>
          <p className="text-sm text-gray-600 leading-relaxed">
            Nous utilisons des cookies et traceurs pour améliorer votre expérience,
            mesurer l'audience et personnaliser nos communications.
            Vous pouvez accepter, refuser ou personnaliser vos choix à tout moment.{' '}
            <Link to="/confidentialite" className="text-green-600 hover:text-green-700 underline">
              Politique de confidentialité
            </Link>
          </p>

          {/* Détails par catégorie */}
          {showDetails && (
            <div className="mt-4 border-t border-gray-100 pt-3 divide-y divide-gray-100">
              <CategoryRow
                id="cookie-necessaires"
                label="Strictement nécessaires"
                description="Authentification, sécurité, fonctionnement du site. Ces cookies ne peuvent pas être désactivés."
                disabled
                checked
              />
              <CategoryRow
                id="cookie-analytique"
                label="Mesure d'audience"
                description="Statistiques d'utilisation, suivi de performance, monitoring technique (Sentry). Nous aide à améliorer le service."
                checked={analytique}
                onChange={setAnalytique}
              />
              <CategoryRow
                id="cookie-marketing"
                label="Communications marketing"
                description="Personnalisation des emails et séquences d'onboarding via Mailchimp. Aucune publicité tierce."
                checked={marketing}
                onChange={setMarketing}
              />
            </div>
          )}
        </div>

        {/* Boutons */}
        <div className="px-5 sm:px-6 pb-5 sm:pb-6 flex flex-wrap gap-3">
          {showDetails ? (
            <>
              <button
                onClick={handleSave}
                className="flex-1 min-w-[140px] px-4 py-2.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2"
              >
                Enregistrer mes choix
              </button>
              <button
                onClick={handleAcceptAll}
                className="flex-1 min-w-[140px] px-4 py-2.5 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2"
              >
                Tout accepter
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleAcceptAll}
                className="flex-1 min-w-[120px] px-4 py-2.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2"
              >
                Tout accepter
              </button>
              <button
                onClick={handleRejectAll}
                className="flex-1 min-w-[120px] px-4 py-2.5 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2"
              >
                Tout refuser
              </button>
              <button
                onClick={() => setShowDetails(true)}
                className="flex-1 min-w-[120px] px-4 py-2.5 text-gray-500 text-sm font-medium rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2"
              >
                Personnaliser
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Panneau de gestion des cookies (accessible depuis le footer).
 * Permet de modifier les préférences après le choix initial.
 */
export function CookieSettingsPanel({ open, onClose }) {
  const { analytique: savedAnalytique, marketing: savedMarketing, savePreferences, acceptAll, rejectAll } = useConsentStore()
  const [analytique, setAnalytique] = useState(savedAnalytique ?? false)
  const [marketing, setMarketing] = useState(savedMarketing ?? false)

  // Resynchroniser l'état local avec le store à chaque ouverture du panneau
  useEffect(() => {
    if (open) {
      setAnalytique(savedAnalytique ?? false)
      setMarketing(savedMarketing ?? false)
    }
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null

  const handleSave = () => {
    savePreferences({ analytique, marketing })
    applyConsent({ analytique, marketing })
    onClose()
  }

  const handleAcceptAll = () => {
    acceptAll()
    applyConsent({ analytique: true, marketing: true })
    onClose()
  }

  const handleRejectAll = () => {
    rejectAll()
    applyConsent({ analytique: false, marketing: false })
    onClose()
  }

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40 z-[9998]" onClick={onClose} />

      {/* Panel */}
      <div
        role="dialog"
        aria-label="Paramètres des cookies"
        aria-modal="true"
        className="fixed inset-x-0 bottom-0 sm:inset-auto sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 z-[9999] bg-white sm:rounded-xl shadow-2xl sm:max-w-lg w-full sm:border border-gray-200"
      >
        <div className="p-5 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-gray-900">Paramètres des cookies</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1"
              aria-label="Fermer"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
            </button>
          </div>

          <p className="text-sm text-gray-600 mb-4">
            Gérez vos préférences de cookies et traceurs.
            Vos choix sont enregistrés localement et modifiables à tout moment.
          </p>

          <div className="divide-y divide-gray-100">
            <CategoryRow
              id="settings-necessaires"
              label="Strictement nécessaires"
              description="Authentification, sécurité, fonctionnement du site."
              disabled
              checked
            />
            <CategoryRow
              id="settings-analytique"
              label="Mesure d'audience"
              description="Statistiques d'utilisation, suivi de performance, monitoring technique (Sentry)."
              checked={analytique}
              onChange={setAnalytique}
            />
            <CategoryRow
              id="settings-marketing"
              label="Communications marketing"
              description="Personnalisation des emails et séquences d'onboarding via Mailchimp."
              checked={marketing}
              onChange={setMarketing}
            />
          </div>
        </div>

        <div className="px-5 sm:px-6 pb-5 sm:pb-6 flex flex-wrap gap-3">
          <button
            onClick={handleSave}
            className="flex-1 min-w-[140px] px-4 py-2.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2"
          >
            Enregistrer mes choix
          </button>
          <button
            onClick={handleAcceptAll}
            className="px-4 py-2.5 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2"
          >
            Tout accepter
          </button>
          <button
            onClick={handleRejectAll}
            className="px-4 py-2.5 text-gray-500 text-sm font-medium rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2"
          >
            Tout refuser
          </button>
        </div>
      </div>
    </>
  )
}
